/**
 * PATH TO GO — normalized NextStep + adapters.
 *
 * Four existing gap producers each speak their own shape:
 *   - coverage      → `RequirementStatus` rows (intent-critical missing rows)
 *   - SED gaps      → `DataGap` re-weighted via `effectiveGapImpact(gap, intent)`
 *   - intent        → `NextInputRecommendation` (rankNextInputs)
 *   - preflight     → blocking section strings (runPreflight) + `preflightRedHelp`
 *   - readiness     → `EvidenceReadiness.recommendations` (string list)
 *
 * This module fuses them onto ONE normalized `NextStep` and provides a single
 * `rankSteps`. REUSE-FIRST: every adapter READS the upstream module's output —
 * it does not re-derive severity, value, or text. Pure + deterministic.
 */
import type { ReportTypeId } from '../../config/reportTypes';
import type { DecisionBandId } from '../portfolio/decisionBands';
import type { DataGap } from '../../types/siteEvidence';
import type { ProjectIntent } from '../../types/siteRag';
import type { RequirementStatus } from '../coverage/requirementStatus';
import type { IntentMissingRow } from '../overview/computeOverviewFindingCards';
import type { NextInputRecommendation } from '../intentIntelligence/nextBestInput';
import {
  effectiveGapImpact,
  SEVERITY_ORDER,
  type ImpactSeverity,
} from '../providerImpactForIntent';
import {
  COVERAGE_DOMAIN_ORDER,
  type CoverageRequirementDomain,
} from '../coverage/requirementDomainGroups';
import { intentSignalsConnectivityDiligence } from './mandatoryReports';
import {
  INTENT_SECTION_SOURCE_FIELD,
  preflightRedHelp,
  SECTION_PRIMARY_PROVIDER,
  type PreflightSection,
} from '../preflightCheck';
import { SED_SECTION_LABELS } from '../sedStaleness';
import type { SedSectionId } from '../../types/siteEvidence';
import {
  explicitUnknownIntentFieldLabel,
  explicitUnknownIntentRecommendation,
  isIntentFieldExplicitlyUnknown,
} from '../intent/explicitUnknown';

/** The kind of action a step represents. */
export type NextStepKind = 'intent' | 'evidence' | 'resolve' | 'generate';

/** Normalized severity, fused across all producers. */
export type NextStepSeverity = 'critical' | 'significant' | 'noted';

/** One normalized, rankable next action. */
export interface NextStep {
  /** Stable, producer-namespaced id (e.g. 'coverage:water-rights'). */
  id: string;
  kind: NextStepKind;
  /** Short, user-facing action label. */
  label: string;
  /** Why this step matters (the actionable reason). */
  why: string;
  severity: NextStepSeverity;
  /** Coverage domain when the step maps to one (drives domain-ordered ranking). */
  domain?: CoverageRequirementDomain;
  /** Checklist ID if this gap can be resolved via contractor outreach. */
  outreachId?: string;
  /**
   * File names of already-uploaded documents that plausibly cover this gap
   * (from `IntentMissingRow.matchedDocs`). Non-empty ⇒ the gap is a
   * VERIFY/EXTRACT gap — surfaces must not ask the user to upload or
   * request a document they already provided.
   */
  matchedDocNames?: readonly string[];
  /** Analyses whose evidence basis would improve when this source is added. */
  affectedAnalysisTypes?: readonly ReportTypeId[];
  /** Whether this item must be resolved before a final positive verdict. */
  decisionRequired?: boolean;
  /** 0–100 estimate of how valuable resolving this step is. */
  valueScore: number;
  /** The band this step would lift the site to, when known (exact projections only). */
  liftsBandTo?: DecisionBandId;
  /** True when the step is a one-click action (e.g. accept a suggested value). */
  oneClick?: boolean;
  /** Which producer this step came from. */
  sourceModule:
    | 'coverage'
    | 'sed-gap'
    | 'intent-recommender'
    | 'preflight'
    | 'evidence-readiness'
    | 'site-basis'
    | 'generation-dependency'
    | 'side-quest'
    | 'screening-question';
  /** Action metadata indicating how the user should resolve this gap. */
  resolver?: {
    label: string;
    /**
     * How the host should resolve the step:
     *   - `open-intent`      → focus the Intent stage (declared-field gaps).
     *   - `open-evidence`    → focus the Site-Evidence surface (data gaps).
     *   - `resolve-in-place` → open the in-place SED-amendment dialog AT THE
     *     VALUE SOURCE (RP2). Carries a `sedPath` (the dotted variablePath to
     *     amend). The host constructs a canonical `SEDAmendment` for `sedPath`
     *     and routes it through the ONE persist path (`saveSEDAmendment`) — so a
     *     resolvable blocker is fixed without navigating away. ONLY set for
     *     steps that map to a CONCRETE, known SED slot (conservative: a
     *     fabricated path is rejected by `applySingleAmendmentToSed`'s
     *     depth/schema guard, surfacing an error rather than corrupting data).
     */
    /**
     *   - `open-tab`         → open a first-class site tab (`targetId` = the
     *     tab id, e.g. 'map', 'simulations', 'generation'). Used by the
     *     site-basis step + the optional side-quests. Hosts that don't
     *     recognize it fall back to the evidence route (compatible extension).
     */
    action: 'open-intent' | 'open-evidence' | 'resolve-in-place' | 'open-tab';
    targetId?: string;
    /**
     * The dotted SED variablePath this step resolves in-place
     * (`action: 'resolve-in-place'` only). Absent for nav-route steps — the
     * host then falls back to the existing open-intent / open-evidence route.
     */
    sedPath?: string;
  };
}

/* ── Severity mapping helpers ───────────────────────────────────────── */

/** Map the intent-aware `ImpactSeverity` onto the NextStep severity. */
function impactToSeverity(impact: ImpactSeverity): NextStepSeverity {
  if (impact === 'critical') return 'critical';
  if (impact === 'significant') return 'significant';
  return 'noted'; // minor | irrelevant
}

/**
 * Map a coverage `RequirementStatus` onto a severity. A wholly-missing
 * intent-critical row is `significant`; a partially-present (tentative/authored)
 * row is `noted` (it has SOME signal). `missing` on a row the intent flags as
 * critical is the worst coverage state we surface here.
 */
function statusToSeverity(status: RequirementStatus): NextStepSeverity {
  if (status === 'missing') return 'significant';
  return 'noted';
}

const SEVERITY_RANK: Record<NextStepSeverity, number> = {
  critical: 2,
  significant: 1,
  noted: 0,
};

/* ── Adapters ───────────────────────────────────────────────────────── */

/** Historical persisted readiness rows now owned by detailed report setup. */
const DECISION_FACT_INTENT_FIELDS: Readonly<Record<string, readonly string[]>> = {
  'decision-fact:firm-capacity-basis-mw': ['powerRequirement'],
  'decision-fact:datacenter-tier-target': ['datacenterTierTarget'],
  'decision-fact:redundancy-class': ['redundancyClass'],
  'decision-fact:time-to-power-critical-date': ['timeline', 'inServiceDate'],
};

/** The decision-stage context the design-readiness adapters word their rows by. */
export interface StepStageContext {
  /** 2 once every required early-screening analysis exists. */
  stage?: 1 | 2;
}

/** Where a design-readiness row is recorded once Stage 2 decisions open. */
const STAGE2_DECISION_FOR_FACT: Readonly<Record<string, string>> = {
  'decision-fact:firm-capacity-basis-mw': 'stage2:firm-capacity',
  'decision-fact:datacenter-tier-target': 'stage2:design-basis',
  'decision-fact:redundancy-class': 'stage2:design-basis',
  'decision-fact:time-to-power-critical-date': 'stage2:build-phases',
  cooling: 'stage2:cooling',
  technology: 'stage2:power-supply',
  firmCapacity: 'stage2:firm-capacity',
};

/**
 * Design-readiness gaps. During early screening they stay limitations, not
 * prerequisites. Once screening is complete they become Stage 2 decisions,
 * routed to the checklist that records them. Neither form is an intent-kind
 * step, so neither reopens Define or pre-empts outstanding analyses.
 */
function deferredDecisionFactStep(id: string, label: string, key: string, context: StepStageContext = {}): NextStep {
  if (context.stage === 2) {
    const decision = STAGE2_DECISION_FOR_FACT[key];
    if (decision) {
      return {
        id,
        kind: 'generate',
        label: `Stage 2 decision: ${label}`,
        why: 'Early screening is complete. Record this input with the Stage 2 decisions so the mature analyses use it; no value has been assumed.',
        severity: 'noted',
        decisionRequired: false,
        valueScore: 20,
        sourceModule: 'coverage',
        resolver: { label: 'Review Stage 2 decisions', action: 'open-intent', targetId: decision },
      };
    }
    if (key === 'lcoe') {
      return {
        id,
        kind: 'generate',
        label: `Stage 2 input: ${label}`,
        why: 'Early screening is complete. Enter the financial model assumptions before the financial analysis; no value has been assumed.',
        severity: 'noted',
        decisionRequired: false,
        valueScore: 18,
        sourceModule: 'coverage',
        resolver: { label: 'Open Financial Model', action: 'open-tab', targetId: 'financial' },
      };
    }
  }
  return {
    id,
    kind: 'generate',
    label: `Later report setup: ${label}`,
    why: 'This design or delivery input can remain unknown during early screening. Record it when preparing the relevant detailed analysis; no value has been assumed.',
    severity: 'noted',
    decisionRequired: false,
    valueScore: 0,
    sourceModule: 'coverage',
    resolver: { label: 'Open report setup', action: 'open-tab', targetId: 'reports' },
  };
}

function recordedUnknownDecisionFactField(
  rowId: string,
  intent: ProjectIntent | null | undefined,
): string | null {
  const fields = DECISION_FACT_INTENT_FIELDS[rowId];
  if (!fields || !intent) return null;
  return fields.find((field) => isIntentFieldExplicitlyUnknown(intent, field)) ?? null;
}

/**
 * Coverage adapter — intent-critical requirement rows (from
 * `computeIntentEvidenceCard().missingRows`, an `IntentMissingRow[]`). Each row
 * already carries its `domain` + `status`.
 *
 * `intent` (optional) only changes WORDING: a decision-fact row whose field the
 * user recorded as unknown keeps its id, kind, severity, value and destination.
 */
export function stepsFromCoverageRows(
  rows: readonly IntentMissingRow[],
  intent?: ProjectIntent | null,
  context: StepStageContext = {},
): NextStep[] {
  return rows.map((row) => {
    if (Object.hasOwn(DECISION_FACT_INTENT_FIELDS, row.id)) {
      return deferredDecisionFactStep(`coverage:${row.id}`, row.label, row.id, context);
    }
    const severity = statusToSeverity(row.status);
    const isIntentFact = row.id.startsWith('decision-fact:');
    const recordedUnknownField = isIntentFact
      ? recordedUnknownDecisionFactField(row.id, intent)
      : null;
    // A row with a matching UPLOADED document is a VERIFY gap, not an
    // UPLOAD gap — asking the user for a document they already provided is
    // the exact trust-killer this branch exists to prevent. The step keeps
    // its outreachId absent so the host never renders the "draft email
    // request" affordance for it.
    // Dedupe by FILE NAME, not docId. The same file can be matched to a
    // requirement through several routes (direct link, tag match, extraction
    // hit) and can also exist as several document records after a re-upload,
    // so the raw list repeats. Live symptom on Redhead: "3 DOCUMENTS UPLOADED"
    // above the same filename printed three times, plus a React duplicate-key
    // error — the count implied three pieces of evidence where there was one.
    // Names are what the user is shown and what they reconcile against, so
    // names are what must be distinct.
    const docNames = !isIntentFact
      ? [...new Set((row.matchedDocs ?? []).map((d) => d.fileName).filter(Boolean))]
      : [];
    const hasUpload = docNames.length > 0;
    return {
      id: `coverage:${row.id}`,
      kind: 'evidence' as const,
      label: recordedUnknownField
        ? `Recorded unknown: ${row.label}`
        : hasUpload ? `Verify evidence: ${row.label}` : `Add evidence: ${row.label}`,
      why: recordedUnknownField
        ? `${explicitUnknownIntentFieldLabel(recordedUnknownField)} was recorded as UNKNOWN — this decision parameter stays open until it is confirmed. `
          + `Follow-up: ${explicitUnknownIntentRecommendation(recordedUnknownField)}`
        : isIntentFact
        ? 'Record this input in the relevant report setup when it becomes known; screening can disclose the limitation.'
        : hasUpload
          // States the FACT only. The action sentence is appended at render
          // time by evidenceStepActionHint(), because the right ask depends on
          // whether extraction produced anything — something this layer cannot
          // know. The old text prescribed "Re-run collection", a button the
          // card may not even carry (founder report, 2026-07-27).
          ? `You've uploaded ${docNames.join(', ')} — the required fields are not yet complete. Review the available values and the remaining gaps.`
          : `Intent-critical coverage row is ${row.status} — fill it to close the evidence gap.`,
      severity,
      domain: row.domain,
      outreachId: !isIntentFact && !hasUpload ? row.id : undefined,
      ...(hasUpload ? { matchedDocNames: docNames } : {}),
      // Coverage rows: significant ≈ 50, noted ≈ 30 (estimate, refined by rank).
      valueScore: severity === 'significant' ? 50 : 30,
      sourceModule: 'coverage' as const,
      resolver: {
        label: recordedUnknownField
          ? 'View recorded unknown'
          : isIntentFact ? 'Go to Intent step' : 'Open Site Evidence',
        action: isIntentFact ? 'open-intent' : 'open-evidence',
        targetId: row.id,
      },
    };
  });
}

/** Q2 — the D3.5 gap id suffix for a section empty by a user-recorded unknown. */
const RECORDED_UNKNOWN_GAP_SUFFIX = ':intent-recorded-unknown';
const INTENT_MISSING_GAP_SUFFIX = ':intent-missing';

/**
 * Q1 / Q2 — is this SED gap a user-recorded unknown rather than a missing input?
 * Such a gap stays on the SED as a record, but it is never turned into a step:
 * recording "unknown" was the user's answer, so there is nothing to ask. The
 * decision hold is carried by `rankNextInputs(...).recordedUnknown` (Define is
 * not cleared while it is non-empty).
 *
 *  - `<section>:intent-recorded-unknown` — the D3.5 record (cooling / power /
 *    firm capacity left empty by a recorded unknown).
 *  - `cooling` while the heat-rejection sink is recorded unknown on the live
 *    intent — the sink gap `assessGaps` raises when nuclear is in scope.
 */
function isRecordedUnknownSedGap(
  gap: DataGap,
  intent: ProjectIntent | null | undefined,
): boolean {
  if (gap.field.endsWith(RECORDED_UNKNOWN_GAP_SUFFIX)) return true;
  // A SED compiled BEFORE the field was recorded unknown (or before this
  // distinction existed) still carries `<section>:intent-missing`. The live
  // intent is the authority — the same fallback preflight applies.
  if (gap.field.endsWith(INTENT_MISSING_GAP_SUFFIX)) {
    const field = INTENT_SECTION_SOURCE_FIELD[gap.field.slice(0, -INTENT_MISSING_GAP_SUFFIX.length)];
    if (field && isIntentFieldExplicitlyUnknown(intent, field)) return true;
  }
  return gap.field === 'cooling' && isIntentFieldExplicitlyUnknown(intent, 'bopHeatRejection');
}

/**
 * SED-gap adapter — raw `DataGap`s re-weighted against intent via
 * `effectiveGapImpact(gap, intent)`. Gaps that the intent makes `irrelevant`
 * are DROPPED (they are not real next steps for this project).
 */
export function stepsFromSedGaps(
  gaps: readonly DataGap[],
  intent: ProjectIntent | null | undefined,
  context: StepStageContext = {},
): NextStep[] {
  const out: NextStep[] = [];
  for (const gap of gaps) {
    if (Object.hasOwn(DECISION_FACT_INTENT_FIELDS, gap.field)) {
      out.push(deferredDecisionFactStep(`sed-gap:${gap.field}`, gap.reason || gap.field, gap.field, context));
      continue;
    }
    if (isRecordedUnknownSedGap(gap, intent)) continue;
    const section = gap.field.split(':')[0]!;
    if (['cooling', 'technology', 'firmCapacity', 'lcoe'].includes(section)
      && (gap.field === section || gap.field.endsWith(INTENT_MISSING_GAP_SUFFIX))) {
      out.push(deferredDecisionFactStep(`sed-gap:${gap.field}`, gap.reason || gap.field, section, context));
      continue;
    }
    const assessed = effectiveGapImpact(gap, intent);
    if (assessed.severity === 'irrelevant') continue;
    const isIntentGap = gap.field === 'cooling';
    out.push({
      id: `sed-gap:${gap.providerId ?? gap.field}`,
      kind: isIntentGap ? 'intent' : 'evidence',
      label: `Resolve data gap: ${gap.field}`,
      why: assessed.reason || gap.recommendation || gap.reason || 'Missing critical data for this site.',
      severity: impactToSeverity(assessed.severity),
      // SED gaps: severity-scaled. critical 70, significant 55, minor 30.
      valueScore: 25 + SEVERITY_ORDER[assessed.severity] * 15,
      sourceModule: 'sed-gap',
      resolver: isIntentGap ? {
        label: 'Go to Intent step',
        action: 'open-intent',
      } : {
        label: 'Open Site Evidence',
        action: 'open-evidence',
        targetId: gap.providerId ?? gap.field,
      },
    });
  }
  return out;
}

/**
 * Intent-recommender adapter — `NextInputRecommendation[]` from `rankNextInputs`.
 * These are "what intent field to fill next"; an inferable field (suggester has
 * a value) is `oneClick`.
 */
export function stepsFromIntentRecommendations(
  recs: readonly NextInputRecommendation[],
): NextStep[] {
  return recs.map((rec) => ({
    id: `intent:${rec.field}`,
    kind: 'intent' as const,
    label: rec.suggestedValue != null
      ? `Set ${rec.field} (suggested: ${rec.suggestedValue})`
      : `Decide ${rec.field}`,
    why: rec.reason,
    // Intent fields don't carry an evidence-gap severity — they are
    // value-ranked. Highest-value field reads as 'significant', the rest 'noted'.
    severity: (rec.valueScore >= 55 ? 'significant' : 'noted') as NextStepSeverity,
    valueScore: rec.valueScore,
    oneClick: rec.inferable,
    sourceModule: 'intent-recommender' as const,
    resolver: {
      label: 'Go to Intent step',
      action: 'open-intent',
    },
  }));
}

/**
 * Preflight adapter — unavailable or stale source sections discovered by the
 * report preflight. They are source-improvement steps: early screening can run
 * with the limitation disclosed, while the decision workflow may still require
 * selected sections before a final GO verdict. Pass the full rows; statuses not
 * selected by the caller are omitted.
 */
export function stepsFromPreflight(
  sections: readonly PreflightSection[],
  blockingStatuses: ReadonlySet<string>,
): NextStep[] {
  const out: NextStep[] = [];
  for (const section of sections) {
    if (!blockingStatuses.has(section.status)) continue;
    // Humanise the section id. `section.id` is a camelCase SED key, so the
    // raw interpolation shipped user-facing labels like "Collect
    // publicSentiment data" — an internal identifier presented as an
    // instruction. SED_SECTION_LABELS is the canonical display map already
    // used by the staleness surfaces; fall back to the id when a section is
    // outside it so an unmapped key degrades to today's text rather than
    // vanishing.
    // Labels carry a parenthetical source list ("Public sentiment
    // (GDELT/Council minutes/Dockets)"), so the old `Collect ${x} data` frame
    // wrapped awkwardly around it. Lead with the subject instead.
    const sectionLabel =
      SED_SECTION_LABELS[section.id as SedSectionId] ?? section.id;
    const isIntentField = SECTION_PRIMARY_PROVIDER[section.id] === 'project-intent';
    // Q3 — an intent-derived section empty because the user RECORDED its field
    // as unknown keeps the step (same id, kind, severity, value, destination:
    // it still holds the decision); only the copy stops asking for the input.
    const recordedUnknown = isIntentField && !!section.recordedUnknownField;
    out.push({
      id: `preflight:${section.id}`,
      kind: 'resolve',
      label: recordedUnknown
        ? `${sectionLabel} — recorded unknown, follow-up open`
        : `${sectionLabel} — not collected`,
      why: section.message || preflightRedHelp(section.id),
      severity: 'critical', // Highest-priority source improvement for a planned analysis.
      valueScore: 75,
      sourceModule: 'preflight',
      resolver: {
        label: recordedUnknown
          ? 'View recorded unknown'
          : isIntentField ? 'Open Project Intent' : 'Open Site Evidence',
        action: isIntentField ? 'open-intent' : 'open-evidence',
        targetId: section.id,
      },
    });
  }
  return out;
}

/**
 * Evidence-readiness adapter — `EvidenceReadiness.recommendations` (a ranked
 * string list, already ordered by score-lift). Index → descending value so the
 * upstream order is preserved.
 */
export function stepsFromReadinessRecommendations(
  recommendations: readonly string[],
): NextStep[] {
  return recommendations.map((rec, i) => {
    let outreachId: string | undefined;
    const lower = rec.toLowerCase();
    if (lower.includes('geotech') || lower.includes('borehole')) outreachId = 'geotech';
    else if (lower.includes('phase 1') || lower.includes('phase i')) outreachId = 'phase1-esa';
    else if (lower.includes('survey') && lower.includes('boundary')) outreachId = 'survey';
    else if (lower.includes('title') || lower.includes('parcel')) outreachId = 'parcel-survey';
    else if (lower.includes('utility') && lower.includes('letter')) outreachId = 'utility-letter';
    else if (lower.includes('wetland') || lower.includes('delineation')) outreachId = 'wetland';
    else if (lower.includes('species') || lower.includes('habitat')) outreachId = 'species-survey';
    else if (lower.includes('cultural') || lower.includes('shpo')) outreachId = 'cultural';
    else if (lower.includes('fiber') && lower.includes('survey')) outreachId = 'fiber-survey';
    else if (lower.includes('zoning')) outreachId = 'zoning';
    else if (lower.includes('water rights') || lower.includes('appropriation')) outreachId = 'water-rights';
    else if (lower.includes('aquifer') || lower.includes('groundwater')) outreachId = 'aquifer-study';
    else if (lower.includes('discharge permit') || lower.includes('npdes')) outreachId = 'discharge-permit';
    // P6 — municipal water / wastewater service statements route to the utility-letter requirement.
    else if (lower.includes('wastewater') || lower.includes('sewer') || lower.includes('water provider') || lower.includes('municipal water')) outreachId = 'municipal-utility-letter';

    return {
      id: `evidence-readiness:${i}`,
      kind: 'evidence' as const,
      label: rec,
      why: 'Raises the site evidence-readiness score.',
      severity: 'noted' as NextStepSeverity,
      outreachId,
      // Upstream is rank-ordered (best first); decay so order survives the merge.
      valueScore: Math.max(10, 40 - i * 5),
      sourceModule: 'evidence-readiness' as const,
      resolver: {
        label: 'Open Site Evidence',
        action: 'open-evidence',
      },
    };
  });
}

/**
 * Drop readiness recommendations that name an evidence item the coverage
 * layer already reports — the same ask twice on one worklist (live symptom:
 * "Upload alta/nsps land title survey (+5)" rendered directly above "Add
 * evidence: ALTA/NSPS land title survey"). Coverage wins: it carries the row
 * status, matched documents, and the outreach affordance; the readiness copy
 * is a rank hint, not a second task. Readiness strings embed the checklist
 * label verbatim and the two modules share no id space, so case-insensitive
 * label containment is the join. Labels under 4 characters are ignored as
 * containment keys — too short to be a meaningful document name.
 */
export function filterReadinessStepsAgainstCoverage(
  readinessSteps: readonly NextStep[],
  coverageRowLabels: readonly string[],
): NextStep[] {
  const keys = coverageRowLabels
    .map((label) => label.trim().toLowerCase())
    .filter((label) => label.length > 3);
  if (keys.length === 0) return [...readinessSteps];
  return readinessSteps.filter((step) => {
    const label = step.label.toLowerCase();
    return !keys.some((key) => label.includes(key));
  });
}

/**
 * Screening questions — open questions the Stage 1 reports raised about site
 * facts (a claim whose evidence state is unknown carries the question and a
 * suggested follow-up). Each becomes an evidence request on the Site Evidence
 * worklist, tagged with its report. Never decision-required: answering one
 * strengthens the mature analyses but no gate waits on it. Deduplicated by
 * question text.
 */
export function stepsFromScreeningQuestions(
  questions: ReadonlyArray<{ question: string; followUp?: string; reportType?: ReportTypeId; reportId?: string }>,
): NextStep[] {
  const seen = new Set<string>();
  const out: NextStep[] = [];
  for (const entry of questions) {
    const question = entry.question.trim();
    const key = question.toLowerCase();
    if (!question || !entry.reportId || seen.has(key)) continue;
    seen.add(key);
    out.push({
      id: `screening-question:${entry.reportId}:${out.length}`,
      kind: 'evidence',
      label: `Screening question: ${question}`,
      why: entry.followUp?.trim()
        || 'Raised by the early screening report. Answering it strengthens the mature analyses.',
      severity: 'noted',
      decisionRequired: false,
      ...(entry.reportType ? { affectedAnalysisTypes: [entry.reportType] } : {}),
      valueScore: 22,
      sourceModule: 'screening-question',
      resolver: { label: 'Open Site Evidence', action: 'open-evidence' },
    });
  }
  return out;
}

/* ── Site-basis + side-quest producers (signal-gated) ───────────────── */
/*
 * These take PRECOMPUTED signals (booleans / messages) rather than importing
 * the predicate modules, so this module stays dependency-light and the
 * orchestrator remains the one place that reads site state. Every producer is
 * signal-gated: a site that doesn't trigger the signal gets an EMPTY array,
 * keeping legacy fixtures byte-stable.
 */

/**
 * Site-basis: boundary geometry. The ONE new required-path step — a drawn
 * boundary feeds the SED parcel/climate providers and the Generation & Impact
 * parcel fit, so a boundary-less site carries an open evidence step.
 */
export function stepsFromSiteBasisGaps(hasBoundary: boolean): NextStep[] {
  if (hasBoundary) return [];
  return [{
    id: 'site-basis:boundary',
    kind: 'evidence',
    label: 'Draw or import the site boundary',
    why: 'Parcel geometry feeds the evidence providers (parcel, climate) and the generation parcel fit — without it those stay regional estimates.',
    severity: 'noted',
    valueScore: 28,
    sourceModule: 'site-basis',
    resolver: { label: 'Open Site Map', action: 'open-tab', targetId: 'map' },
  }];
}

/**
 * OPTIONAL side-quests — never merged into `rankedSteps`, never counted in
 * stage open-counts, never gating GO. They strengthen the case; the wizard
 * renders them in a clearly-labelled "Optional deep-dives" surface.
 */
export function stepsFromAnalysisGaps(input: {
  hasSimulation: boolean;
  intent: ProjectIntent | null | undefined;
}): NextStep[] {
  // Signal: a declared power target and no simulation yet.
  if (input.hasSimulation) return [];
  if (input.intent?.powerRequirementMW == null) return [];
  return [{
    id: 'analysis:engineering',
    kind: 'generate',
    label: 'Run an engineering simulation',
    why: 'Model the plant on the canvas to validate the power concept end-to-end.',
    severity: 'noted',
    valueScore: 24,
    sourceModule: 'side-quest',
    resolver: { label: 'Open Engineering Analysis', action: 'open-tab', targetId: 'simulations' },
  }];
}

/**
 * CONNECTIVITY-ADVISORY (2026-08-25) — the optional Connectivity deep-dive.
 *
 * While `CONNECTIVITY_GATES_GO` is false the Connectivity Feasibility Study is
 * never in the GO gate, so the SAME declared-intent signals that used to
 * require it (`intentSignalsConnectivityDiligence` — active queue position,
 * nuclear interest, speed/resilience priority) now suggest it as a side quest
 * instead. `connectivityRequired` guards the flag flip: once the study is
 * re-gated into the required set it gets a real required row and this quest
 * must not double-surface it.
 */
export function stepsFromConnectivityGaps(input: {
  intent: ProjectIntent | null | undefined;
  /** True when 'interconnection' is in the required-for-GO set (flag flipped). */
  connectivityRequired: boolean;
  /** True when a Connectivity Feasibility Study already exists for the site. */
  hasConnectivityReport: boolean;
}): NextStep[] {
  if (input.connectivityRequired || input.hasConnectivityReport) return [];
  if (!intentSignalsConnectivityDiligence(input.intent ?? undefined)) return [];
  return [{
    id: 'analysis:connectivity',
    kind: 'generate',
    label: 'Run the Connectivity Feasibility Study',
    why: 'Your declared priority makes the fiber/carrier posture worth a deep-dive — advisory only: connectivity does not gate the GO/NO-GO call (insufficient data coverage).',
    severity: 'noted',
    valueScore: 25,
    sourceModule: 'side-quest',
    resolver: { label: 'Open Analyses', action: 'open-tab', targetId: 'reports' },
  }];
}

export function stepsFromFinancialGaps(input: { financialAssumptionsSet: boolean }): NextStep[] {
  if (input.financialAssumptionsSet) return [];
  return [{
    id: 'financial:assumptions',
    kind: 'generate',
    label: 'Enter financial assumptions',
    why: 'Unlocks pathway, cashflow, and sensitivity analysis for this site.',
    severity: 'noted',
    valueScore: 22,
    sourceModule: 'side-quest',
    resolver: { label: 'Open Financial Model', action: 'open-tab', targetId: 'financial' },
  }];
}

export function stepsFromBomGaps(input: { supplyChainReady: boolean }): NextStep[] {
  if (input.supplyChainReady) return [];
  return [{
    id: 'bom:setup',
    kind: 'generate',
    label: 'Start the equipment BOM',
    why: 'A real bill of materials grounds procurement lead times and CAPEX in the financial model.',
    severity: 'noted',
    valueScore: 20,
    sourceModule: 'side-quest',
    resolver: { label: 'Open Equipment (BOM)', action: 'open-tab', targetId: 'supplychain' },
  }];
}

export function stepsFromGenerationGaps(input: {
  /** `testFitWorkflowBlockMessage(site, sed)` — null means the chain is done. */
  blockMessage: string | null;
  /** The generation tab is feature-gated; no quest when it isn't visible. */
  enabled: boolean;
  /** Required Test Fit promotes this from optional deep-dive to Analyze gate. */
  required?: boolean;
}): NextStep[] {
  if (!input.enabled || input.blockMessage == null) return [];
  return [{
    id: 'generation:testfit',
    kind: 'generate',
    label: input.required
      ? 'Complete Generation & Impact for Test Fit'
      : 'Complete the generation test fit',
    why: input.blockMessage,
    severity: input.required ? 'critical' : 'noted',
    valueScore: input.required ? 96 : 26,
    sourceModule: input.required ? 'generation-dependency' : 'side-quest',
    resolver: { label: 'Open Generation & Impact', action: 'open-tab', targetId: 'generation' },
  }];
}

export function stepsFromRoadmapGaps(input: { completed: number; total: number }): NextStep[] {
  if (input.total === 0 || input.completed >= input.total) return [];
  return [{
    id: 'roadmap:execution',
    kind: 'generate',
    label: `Track roadmap execution (${input.completed}/${input.total} done)`,
    why: 'Current analyses carry recommendations that are not yet completed — the Roadmap tracks their execution.',
    severity: 'noted',
    valueScore: 18,
    sourceModule: 'side-quest',
    resolver: { label: 'Open Roadmap', action: 'open-tab', targetId: 'roadmap' },
  }];
}

/* ── Ranking ────────────────────────────────────────────────────────── */

function domainOrderIndex(domain: CoverageRequirementDomain | undefined): number {
  if (!domain) return COVERAGE_DOMAIN_ORDER.length;
  const i = COVERAGE_DOMAIN_ORDER.indexOf(domain);
  return i < 0 ? COVERAGE_DOMAIN_ORDER.length : i;
}

/**
 * The band-lift ordinal — a step that lifts the site to a BETTER band ranks
 * higher. Lower decisionBandId index = better band (go < conditional < …), so
 * we map to a descending priority. Steps with no known lift sort after lifters.
 */
const BAND_LIFT_PRIORITY: Record<DecisionBandId, number> = {
  go: 4,
  conditional: 3,
  conditionalHigh: 2,
  noGo: 1,
};

function bandLiftPriority(band: DecisionBandId | undefined): number {
  return band ? BAND_LIFT_PRIORITY[band] : 0;
}

/**
 * Rank steps: critical-first, then band-lift (a step that lifts to a better band
 * outranks one that doesn't), then value, then coverage-domain order, then a
 * stable id tie-break. Deterministic for identical inputs.
 */
export function rankSteps(steps: readonly NextStep[]): NextStep[] {
  return [...steps].sort((a, b) => {
    // 1. severity (critical first)
    const sev = SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity];
    if (sev !== 0) return sev;
    // 2. band-lift desc (a step that lifts to a better band first)
    const lift = bandLiftPriority(b.liftsBandTo) - bandLiftPriority(a.liftsBandTo);
    if (lift !== 0) return lift;
    // 3. value desc
    if (b.valueScore !== a.valueScore) return b.valueScore - a.valueScore;
    // 4. coverage-domain order
    const dom = domainOrderIndex(a.domain) - domainOrderIndex(b.domain);
    if (dom !== 0) return dom;
    // 5. stable id tie-break
    return a.id.localeCompare(b.id);
  });
}
